import { useEffect, useState } from 'react'
import { Router, useRouter } from 'next/router'
import { SmartLink } from '../SmartLink'
import { NavNode, PrimaryNavElements, SecondaryNavigation } from '../const'
import { ChevronIcon, CloseIcon, ExternalIcon } from '../Icons'
import { VoidCallback } from 'types/index'
import { Actionable, Icon, Link, Modal, Text, View } from 'reshaped/bundle'

interface Props {
	onClose: VoidCallback
	isMenuOpen: boolean
}

export const MobileMenu = ({ onClose, isMenuOpen }: Props) => {
	const router = useRouter()
	const [activeNavItem, setActiveNavItem] = useState('')

	useEffect(() => {
		const handler = () => {
			setActiveNavItem('')
			window.scrollTo({ top: 0, behavior: 'smooth' })
		}

		Router.events.on('routeChangeComplete', handler)

		return () => {
			Router.events.off('routeChangeComplete', handler)
			document.body.style.overflow = 'scroll'
		}
	}, [onClose])

	const onNavItemClick = (item: NavNode) => {
		if (item.children) {
			setActiveNavItem(item.key === activeNavItem ? '' : item.key)
		} else {
			onClose()
			router.push(item.href)
		}
	}

	return (
		<Modal active={isMenuOpen} onClose={onClose} position="start" size="80%">
			<View align="end" paddingBottom={4}>
				<Actionable onClick={onClose}>
					<Icon svg={CloseIcon} size={5} />
				</Actionable>
			</View>
			<View direction="column" gap={6}>
				<View direction="column" gap={4}>
					{PrimaryNavElements.filter(element => element.hideByDevice !== 'mobile').map(navItem => {
						const active = navItem.key === activeNavItem
						return (
							<Actionable key={navItem.key} onClick={() => onNavItemClick(navItem)}>
								<View
									borderColor={activeNavItem ? 'neutral' : 'neutral-faded'}
									borderRadius="medium"
									paddingBlock={2}
									paddingInline={4}
								>
									<View direction="row" align={active ? 'stretch' : 'center'} width="100%">
										<View.Item grow>
											<View direction="row" gap={4} align="center" paddingBottom={active ? 4 : 0}>
												<View width="32px" height="32px">
													{navItem.icon}
												</View>
												<Text color="neutral">{navItem.name}</Text>
											</View>
										</View.Item>
										<View.Item>{navItem.children?.length && <Icon svg={ChevronIcon} size={4} />}</View.Item>
									</View>
									{active && navItem.children && (
										<View direction="column" gap={4}>
											{navItem.children.map(item => (
												<View key={item.key} direction="row" align="center" gap={1}>
													<div style={{ width: '48px' }} />
													<SmartLink node={item}>
														<Link
															variant="plain"
															color="inherit"
															href={item.href}
															attributes={{ target: item.external ? '_blank' : undefined }}
														>
															{item.name}
														</Link>
													</SmartLink>
													{item.external && <Icon svg={ExternalIcon} />}
												</View>
											))}
										</View>
									)}
								</View>
							</Actionable>
						)
					})}
				</View>
				<View direction="row" align="center" gap={4}>
					{SecondaryNavigation.filter(node => node.name).map(node => (
						<SmartLink node={node} key={node.key}>
							<Link
								variant="plain"
								color="inherit"
								href={node.href}
								attributes={{
									style: {
										width: '40%',
										display: 'flex',
										gap: '4px',
										alignItems: 'center'
									}
								}}
							>
								{node.name} {node.external && <Icon svg={ExternalIcon} />}
							</Link>
						</SmartLink>
					))}
				</View>
			</View>
		</Modal>
	)
}
